

import { useClicksterStore } from '@/src/stores';
import { ProfileModalActions } from '@/src/stores/general';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/src/components/ui/shadcn/tooltip";
import { useNotifications } from '@/src/hooks/use-notifications';

/**
 * User avatar component for spotlight interface
 * Displays current user avatar and provides navigation to profile settings
 * Shows notification badge with unseen count
 */
export const UserAvatar = () => {
    const { userInSession: user } = useClicksterStore(state => state);
    const { unseenCount, isLoadingCount } = useNotifications();

    /**
     * Handle avatar click to open profile modal
     */
    const handleAvatarClick = () => {
        ProfileModalActions.open();
    };

    /**
     * Get display name for tooltip
     */
    const getDisplayName = () => {
        if (!user) return ''; // Mock name when no user
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        return `${firstName} ${lastName}`.trim() || user.email || 'User';
    };

    /**
     * Get user initials for avatar fallback
     */
    const getUserInitials = () => {
        if (!user) return ''; 
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        if (firstName) return firstName.charAt(0).toUpperCase();
        if (user.email) return user.email.charAt(0).toUpperCase();
        return 'U';
    };

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="relative">
                        <button
                            data-testid="spotlight-user-avatar-button"
                            onClick={handleAvatarClick}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer border border-gray-200 dark:border-gray-600"
                            aria-label="Open profile settings"
                        >
                            {user?.media?.url ? (
                                <img
                                    data-testid="spotlight-user-avatar-image"
                                    src={user.media.url}
                                    alt={getDisplayName()}
                                    className="w-full h-full rounded-full object-cover"
                                />
                            ) : (
                                <div
                                    data-testid="spotlight-user-avatar-initials"
                                    className="flex items-center justify-center w-full h-full text-xs font-medium text-gray-600 dark:text-gray-300"
                                >
                                    {getUserInitials()}
                                </div>
                            )}
                        </button>
                        {!isLoadingCount && unseenCount > 0 && (
                            <div
                                data-testid="spotlight-user-avatar-badge"
                                className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#6366f1] text-white text-[10px] font-semibold border-2 border-white dark:border-gray-800"
                            >
                                {unseenCount > 99 ? '99+' : unseenCount}
                            </div>
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p data-testid="spotlight-user-avatar-tooltip">{getDisplayName()}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default UserAvatar;
